/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {Diff} from './github/diffTypes';

import {gitHubPullRequestFileStatsAtom} from './jotai';
import scrollToFileElement from './scrollToFileElement';
import {getPathForChange, getOldPathForChange} from './utils';
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react';
import {Box, Text} from '@primer/react';
import {useAtomValue} from 'jotai';
import {useCallback, useRef, useState} from 'react';

/** Collapse the ToC by default when the PR has more than this many files. */
const AUTO_COLLAPSE_THRESHOLD = 50;

type Props = {
  diff: Diff;
};

function changeTypeLabel(type: 'add' | 'remove' | 'modify' | 'rename'): {label: string; color: string} {
  switch (type) {
    case 'add':
      return {label: 'A', color: '#6ab04c'};
    case 'remove':
      return {label: 'D', color: '#e74c3c'};
    case 'modify':
      return {label: 'M', color: '#f0c040'};
    case 'rename':
      return {label: 'R', color: '#8e44ad'};
  }
}

export default function DiffTableOfContents({diff}: Props): React.ReactElement | null {
  const fileStatsByPath = useAtomValue(gitHubPullRequestFileStatsAtom);
  const [open, setOpen] = useState(diff.length <= AUTO_COLLAPSE_THRESHOLD);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(() => {
    setOpen(prev => {
      if (prev) {
        // Collapsing — scroll the container to the top so we don't end up
        // stranded below the (now-removed) file list.
        requestAnimationFrame(() => {
          containerRef.current?.scrollIntoView({behavior: 'auto', block: 'start'});
        });
      }
      return !prev;
    });
  }, []);

  if (diff.length === 0) {
    return null;
  }

  const counts = {add: 0, modify: 0, remove: 0, rename: 0};
  for (const change of diff) {
    counts[change.type]++;
  }

  return (
    <Box
      ref={containerRef}
      data-toc-container
      sx={{
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: 2,
      }}>
      <Box
        onClick={handleToggle}
        sx={{
          bg: '#34495e',
          color: '#ecf0f1',
          px: 3,
          py: 2,
          fontSize: '13px',
          fontWeight: 'bold',
          borderBottom: open ? '1px solid' : 'none',
          borderColor: 'border.default',
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2,
          // When collapsed, also round the bottom corners
          ...(!open ? {borderBottomLeftRadius: 2, borderBottomRightRadius: 2} : {}),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          position: open ? ('sticky' as const) : ('static' as const),
          top: 0,
          zIndex: open ? 11 : undefined,
        }}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          {open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
          <span>Table of Contents ({diff.length} {diff.length === 1 ? 'file' : 'files'})</span>
        </Box>
        <Box sx={{display: 'flex', gap: 2, fontSize: '12px', fontWeight: 'normal'}}>
          {counts.add > 0 && <ChangeCount label="A" color="#6ab04c" count={counts.add} />}
          {counts.modify > 0 && <ChangeCount label="M" color="#f0c040" count={counts.modify} />}
          {counts.remove > 0 && <ChangeCount label="D" color="#e74c3c" count={counts.remove} />}
          {counts.rename > 0 && <ChangeCount label="R" color="#8e44ad" count={counts.rename} />}
        </Box>
      </Box>
      {open && <Box sx={{bg: 'canvas.default'}}>
        {diff.map((change, index) => {
          const path = getPathForChange(change);
          const oldPath = getOldPathForChange(change);
          const {label, color} = changeTypeLabel(change.type);
          return (
            <Box
              key={path}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                px: 3,
                py: '6px',
                fontSize: '13px',
                fontFamily: 'mono',
                borderBottom: index < diff.length - 1 ? '1px solid' : 'none',
                borderColor: 'border.subtle',
                ':hover': {bg: 'canvas.subtle'},
                cursor: 'pointer',
              }}
              onClick={() => scrollToFileElement(path)}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '20px',
                  height: '20px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  fontFamily: 'normal',
                  color: '#fff',
                  bg: color,
                  flexShrink: 0,
                }}>
                {label}
              </Box>
              <Text sx={{color: 'fg.default', fontSize: '12px', flexGrow: 1}}>
                {oldPath != null && (
                  <>
                    <Text sx={{color: 'fg.muted', fontSize: '11px', mr: 1}}>
                      {oldPath}
                    </Text>
                    <Text sx={{color: 'fg.muted', fontSize: '11px', mr: 1}}>→</Text>
                  </>
                )}
                {path}
              </Text>
              <FileStats stats={fileStatsByPath.get(path)} />
            </Box>
          );
        })}
      </Box>}
    </Box>
  );
}

function ChangeCount({label, color, count}: {label: string; color: string; count: number}): React.ReactElement {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: '4px'}}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '3px',
          bg: color,
          color: '#fff',
          fontSize: '10px',
          fontWeight: 'bold',
          lineHeight: 1,
        }}>
        {label}
      </Box>
      <Text sx={{color: '#ecf0f1'}}>{count}</Text>
    </Box>
  );
}

function FileStats({
  stats,
}: {
  stats: {additions: number; deletions: number} | undefined;
}): React.ReactElement | null {
  if (stats == null || (stats.additions === 0 && stats.deletions === 0)) {
    return null;
  }
  return (
    <Box sx={{display: 'flex', gap: '6px', flexShrink: 0, fontSize: '12px', fontFamily: 'normal'}}>
      {stats.additions > 0 && (
        <Text sx={{color: '#6ab04c', fontWeight: 'bold'}}>+{stats.additions}</Text>
      )}
      {stats.deletions > 0 && (
        <Text sx={{color: '#e74c3c', fontWeight: 'bold'}}>&minus;{stats.deletions}</Text>
      )}
    </Box>
  );
}
